<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="6" class="page-header-card rounded-xxl">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon color="primary" size="28" class="mr-3">mdi-account-multiple-plus</v-icon>
                    <div>
                      <h1 class="page-title primary--text">Seller Product Variant Mapper</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Advanced bulk tool for mapping sellers to product variants with pricing and inventory
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Action Buttons -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="action-button mr-2"
                      small
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back to List
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="processBulkMapping"
                      :loading="isProcessing"
                      :disabled="!hasValidMappings || isProcessing"
                      small
                    >
                      <v-icon left small>mdi-check-all</v-icon>
                      Apply Mappings
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Selection Controls Card -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="6" class="control-card rounded-xxl">
            <v-card-title class="py-1 px-2">
              <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
              Selection & Global Settings
            </v-card-title>
            <v-card-text class="py-1 px-2">
              <v-row>
                <!-- Product Variant Selection -->
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="selectedProductVariants"
                    :items="productVariantItems"
                    label="Select Product Variants"
                    placeholder="Choose variants to map"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    hide-details
                    @input="onProductVariantsChange"
                    class="professional-field"
                  >
                    <template #prepend-inner>
                      <v-icon color="grey">mdi-package-variant</v-icon>
                    </template>
                    <template #selection="{ item, index }">
                      <v-chip v-if="index < 2" small close @click:close="removeProductVariant(item)">
                        {{ item.text.substring(0, 25) }}{{ item.text.length > 25 ? '...' : '' }}
                      </v-chip>
                      <span v-if="index === 2" class="text-caption grey--text">
                        (+{{ selectedProductVariants.length - 2 }} others)
                      </span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Seller Selection -->
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="selectedSellers"
                    :items="sellerItems"
                    label="Select Sellers"
                    placeholder="Choose sellers to map"
                    outlined
                    dense
                    multiple
                    chips
                    deletable-chips
                    hide-details
                    @input="onSellersChange"
                    class="professional-field"
                  >
                    <template #prepend-inner>
                      <v-icon color="grey">mdi-account-multiple</v-icon>
                    </template>
                    <template #selection="{ item, index }">
                      <v-chip v-if="index < 2" small close @click:close="removeSeller(item)">
                        {{ item.text.substring(0, 25) }}{{ item.text.length > 25 ? '...' : '' }}
                      </v-chip>
                      <span v-if="index === 2" class="text-caption grey--text">
                        (+{{ selectedSellers.length - 2 }} others)
                      </span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Global Defaults Toggle -->
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="useGlobalDefaults"
                    label="Apply Global Defaults"
                    inset
                    color="primary"
                    hide-details
                    class="mt-1"
                  ></v-switch>
                  <div class="text-caption grey--text mt-1">
                    Use default pricing and settings for all mappings
                  </div>
                </v-col>
              </v-row>

              <!-- Global Default Settings (shown when toggle is on) -->
              <v-expand-transition>
                <v-row v-show="useGlobalDefaults" class="mt-2">
                  <v-col cols="12">
                    <v-divider class="mb-3"></v-divider>
                    <h4 class="mb-2">
                      <v-icon color="primary" class="mr-2">mdi-tune</v-icon>
                      Global Default Settings
                    </h4>
                  </v-col>
                  
                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.maximum_retail_price"
                      label="Maximum Retail Price"
                      outlined
                      dense
                      type="number"
                      step="0.01"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-currency-inr</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.seller_discount"
                      label="Seller Discount"
                      outlined
                      dense
                      type="number"
                      step="0.01"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-tag-minus</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.ht_discount"
                      label="HT Discount"
                      outlined
                      dense
                      type="number"
                      step="0.01"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-sale</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.consumer_discount"
                      label="Consumer Discount"
                      outlined
                      dense
                      type="number"
                      step="0.01"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-account-cash</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.doctor_discount"
                      label="Doctor Discount"
                      outlined
                      dense
                      type="number"
                      step="0.01"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-medical-bag</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  
                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model="globalDefaults.stock"
                      label="Default Stock"
                      outlined
                      dense
                      type="number"
                      min="0"
                      hide-details
                    >
                      <template #prepend-inner>
                        <v-icon color="grey" small>mdi-package</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  
                  <v-col cols="6" md="3">
                    <v-switch
                      v-model="globalDefaults.status"
                      label="Selling Status"
                      inset
                      color="success"
                      hide-details
                      class="mt-1"
                    ></v-switch>
                  </v-col>
                  
                  <v-col cols="6" md="3">
                    <v-switch
                      v-model="globalDefaults.is_active"
                      label="Active Status"
                      inset
                      color="success"
                      hide-details
                      class="mt-1"
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mapping Matrix Card -->
      <v-row v-if="mappingMatrix.length > 0">
        <v-col cols="12">
          <v-card elevation="6" class="matrix-card rounded-xxl">
            <v-card-title class="py-1 px-2">
              <v-icon color="primary" class="mr-2">mdi-grid</v-icon>
              Mapping Matrix
              <v-spacer></v-spacer>
              <v-chip small color="info" class="mr-2">
                {{ mappingMatrix.length }} Total Mappings
              </v-chip>
              <v-chip small :color="hasValidMappings ? 'success' : 'warning'">
                {{ validMappingCount }} Valid
              </v-chip>
            </v-card-title>
            
            <v-card-text class="pa-0">
              <v-data-table
                :headers="matrixHeaders"
                :items="mappingMatrix"
                :loading="isLoadingMatrix"
                fixed-header
                height="400"
                class="mapping-table"
                hide-default-footer
                disable-pagination
              >
                <!-- Seller Column -->
                <template #[`item.seller`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="24" class="mr-2" color="primary">
                      <span class="white--text text-caption">
                        {{ getSellerInitials(item.seller) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2">
                        {{ getSellerName(item.seller) }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Product Variant Column -->
                <template #[`item.product_variant`]="{ item }">
                  <div>
                    <div class="font-weight-medium text-body-2">
                      {{ item.product_variant.text }}
                    </div>
                  </div>
                </template>

                <!-- MRP Column -->
                <template #[`item.maximum_retail_price`]="{ item }">
                  <v-text-field
                    v-model="item.maximum_retail_price"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Selling Price Column -->
                <template #[`item.selling_price`]="{ item }">
                  <v-text-field
                    v-model="item.selling_price"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="onSellingPriceChange(item)"
                  ></v-text-field>
                </template>

                <!-- Discount Price Column -->
                <template #[`item.ht_discount`]="{ item }">
                  <v-text-field
                    v-model="item.ht_discount"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Commission % Column -->
                <template #[`item.seller_discount`]="{ item }">
                  <v-text-field
                    v-model="item.seller_discount"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Consumer Discount Column -->
                <template #[`item.consumer_discount`]="{ item }">
                  <v-text-field
                    v-model="item.consumer_discount"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Doctor Discount Column -->
                <template #[`item.doctor_discount`]="{ item }">
                  <v-text-field
                    v-model="item.doctor_discount"
                    type="number"
                    step="0.01"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Stock Column -->
                <template #[`item.stock`]="{ item }">
                  <v-text-field
                    v-model="item.stock"
                    type="number"
                    min="0"
                    dense
                    outlined
                    hide-details
                    class="matrix-field"
                    @input="validateMapping(item)"
                  ></v-text-field>
                </template>

                <!-- Status Columns -->
                <template #[`item.status`]="{ item }">
                  <v-switch
                    v-model="item.status"
                    inset
                    dense
                    hide-details
                    color="success"
                    @change="validateMapping(item)"
                  ></v-switch>
                </template>

                <template #[`item.is_active`]="{ item }">
                  <v-switch
                    v-model="item.is_active"
                    inset
                    dense
                    hide-details
                    color="success"
                    @change="validateMapping(item)"
                  ></v-switch>
                </template>

                <!-- Validation Status Column -->
                <template #[`item.validation_status`]="{ item }">
                  <v-chip
                    x-small
                    :color="item.is_valid ? 'success' : 'error'"
                    :text-color="item.is_valid ? 'white' : 'white'"
                  >
                    <v-icon left x-small>
                      {{ item.is_valid ? 'mdi-check' : 'mdi-alert' }}
                    </v-icon>
                    {{ item.is_valid ? 'Valid' : 'Invalid' }}
                  </v-chip>
                  <div v-if="!item.is_valid && item.validation_errors" class="text-caption error--text mt-1">
                    {{ item.validation_errors.join(', ') }}
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <v-row v-else>
        <v-col cols="12">
          <v-card elevation="6" class="empty-state-card rounded-xxl text-center">
            <v-card-text class="py-8">
              <v-icon color="grey" size="64" class="mb-4">mdi-account-plus</v-icon>
              <h3 class="grey--text mb-2">No Mappings to Display</h3>
              <p class="grey--text text--darken-1 mb-4">
                Select product variants and sellers above to create seller-variant mappings
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Processing Results Dialog -->
      <v-dialog v-model="resultsDialog.show" max-width="600" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="pa-2">
            <v-icon :color="resultsDialog.success ? 'success' : 'warning'" class="mr-2">
              {{ resultsDialog.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="text-h6">Bulk Mapping Results</span>
          </v-card-title>
          
          <v-card-text class="pa-2">
            <div class="mb-3">
              <v-chip small color="success" class="mr-2">
                <v-icon left small>mdi-check</v-icon>
                {{ resultsDialog.created }} Created
              </v-chip>
              <v-chip small color="warning" class="mr-2">
                <v-icon left small>mdi-alert</v-icon>
                {{ resultsDialog.skipped }} Skipped
              </v-chip>
              <v-chip small color="error">
                <v-icon left small>mdi-close</v-icon>
                {{ resultsDialog.failed }} Failed
              </v-chip>
            </div>

            <div v-if="resultsDialog.errors.length > 0" class="mt-3">
              <h4 class="mb-2">Errors:</h4>
              <v-list dense>
                <v-list-item v-for="(error, index) in resultsDialog.errors" :key="index">
                  <v-list-item-icon>
                    <v-icon color="error" small>mdi-alert-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <div class="text-caption">{{ error }}</div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
          
          <v-card-actions class="pa-2">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="closeResultsDialog">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductVariantMapperPage",
  data() {
    return {
      selectedProductVariants: [],
      selectedSellers: [],
      sellerItems: [],
      productVariantItems: [],
      useGlobalDefaults: false,
      globalDefaults: {
        maximum_retail_price: null,
        seller_discount: null,
        ht_discount: null,
        consumer_discount: null,
        doctor_discount: null,
        stock: 0,
        status: true,
        is_active: true
      },
      mappingMatrix: [],
      isLoadingMatrix: false,
      isProcessing: false,
      matrixHeaders: [
        { text: 'Seller', value: 'seller', sortable: false, width: '160px' },
        { text: 'Product Variant', value: 'product_variant', sortable: false, width: '180px' },
        { text: 'MRP', value: 'maximum_retail_price', sortable: false, width: '100px' },
        { text: 'Seller Disc.', value: 'seller_discount', sortable: false, width: '90px' },
        { text: 'HT Disc.', value: 'ht_discount', sortable: false, width: '90px' },
        { text: 'Consumer Disc.', value: 'consumer_discount', sortable: false, width: '100px' },
        { text: 'Doctor Disc.', value: 'doctor_discount', sortable: false, width: '95px' },
        { text: 'Stock', value: 'stock', sortable: false, width: '80px' },
        { text: 'Selling', value: 'status', sortable: false, width: '70px' },
        { text: 'Active', value: 'is_active', sortable: false, width: '70px' },
        { text: 'Status', value: 'validation_status', sortable: false, width: '100px' }
      ],
      resultsDialog: {
        show: false,
        success: false,
        created: 0,
        skipped: 0,
        failed: 0,
        errors: []
      }
    };
  },
  computed: {
    hasValidMappings() {
      return this.mappingMatrix.some(item => item.is_valid);
    },
    validMappingCount() {
      return this.mappingMatrix.filter(item => item.is_valid).length;
    }
  },
  async mounted() {
    await this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`/masters?context=seller_product_variant`);
        const masters = response.data;

        // Populate sellers
        this.sellerItems = masters.users ? masters.users.map(user => ({
          value: user.id,
          text: `${user.first_name} ${user.last_name} (${user.email})`,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          id: user.id
        })) : [];

        // Populate product variants
        this.productVariantItems = masters.product_variants ? masters.product_variants.map(variant => ({
          value: variant.id,
          text: `${variant.name} - ${variant.sku}`,
          name: variant.name,
          sku: variant.sku,
          maximum_retail_price: variant.maximum_retail_price || 0,
          id: variant.id
        })) : [];

      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        this.$toast.error('Failed to load master data');
      }
    },

    onProductVariantsChange() {
      this.generateMappingMatrix();
    },

    onSellersChange() {
      this.generateMappingMatrix();
    },

    removeProductVariant(item) {
      const index = this.selectedProductVariants.findIndex(v => v.value === item.value);
      if (index > -1) {
        this.selectedProductVariants.splice(index, 1);
        this.generateMappingMatrix();
      }
    },

    removeSeller(item) {
      const index = this.selectedSellers.findIndex(s => s.value === item.value);
      if (index > -1) {
        this.selectedSellers.splice(index, 1);
        this.generateMappingMatrix();
      }
    },

    async generateMappingMatrix() {
      if (this.selectedProductVariants.length === 0 || this.selectedSellers.length === 0) {
        this.mappingMatrix = [];
        return;
      }

      this.isLoadingMatrix = true;

      try {
        // Check for existing mappings to prevent duplicates
        const existingMappings = await this.getExistingMappings();
        
        this.mappingMatrix = [];

        for (const seller of this.selectedSellers) {
          for (const variant of this.selectedProductVariants) {
            // Check if mapping already exists
            const existsKey = `${seller.value}_${variant.value}`;
            const alreadyExists = existingMappings.has(existsKey);

            const mapping = {
              seller: seller,
              product_variant: variant,
              seller_id: seller.value,
              product_variant_id: variant.value,
              seller_product_id: null, // Can be set to link to existing seller product if needed
              maximum_retail_price: this.useGlobalDefaults ? this.globalDefaults.maximum_retail_price : (variant.maximum_retail_price || 0),
              seller_discount: this.useGlobalDefaults ? this.globalDefaults.seller_discount : null,
              ht_discount: this.useGlobalDefaults ? this.globalDefaults.ht_discount : null,
              consumer_discount: this.useGlobalDefaults ? this.globalDefaults.consumer_discount : null,
              doctor_discount: this.useGlobalDefaults ? this.globalDefaults.doctor_discount : null,
              stock: this.useGlobalDefaults ? this.globalDefaults.stock : 0,
              status: this.useGlobalDefaults ? this.globalDefaults.status : true,
              is_active: this.useGlobalDefaults ? this.globalDefaults.is_active : true,
              is_valid: false,
              validation_errors: [],
              already_exists: alreadyExists
            };

            // Validate initial mapping
            this.validateMapping(mapping);

            this.mappingMatrix.push(mapping);
          }
        }
      } catch (error) {
        console.error('Error generating matrix:', error);
        this.$toast.error('Failed to generate mapping matrix');
      } finally {
        this.isLoadingMatrix = false;
      }
    },

    async getExistingMappings() {
      try {
        const sellerIds = this.selectedSellers.map(s => s.value);
        const variantIds = this.selectedProductVariants.map(v => v.value);
        
        const response = await axios.post('/seller_product_variants/check_existing', {
          seller_ids: sellerIds,
          product_variant_ids: variantIds
        });

        const existingSet = new Set();
        if (response.data.success && response.data.data) {
          response.data.data.forEach(mapping => {
            existingSet.add(`${mapping.seller_id}_${mapping.product_variant_id}`);
          });
        }
        
        return existingSet;
      } catch (error) {
        console.error('Error checking existing mappings:', error);
        return new Set();
      }
    },

    onSellingPriceChange(item) {
      this.validateMapping(item);
    },


    validateMapping(item) {
      const errors = [];
      
      if (!item.seller_id) errors.push('Seller required');
      if (!item.product_variant_id) errors.push('Product variant required');
      if (!item.maximum_retail_price || parseFloat(item.maximum_retail_price) <= 0) errors.push('Valid selling price required');
      if (item.ht_discount && parseFloat(item.ht_discount) >= parseFloat(item.maximum_retail_price)) {
        errors.push('HT discount must be less than maximum retail price');
      }
      if (item.seller_discount && parseFloat(item.seller_discount) < 0) {
        errors.push('Seller discount cannot be negative');
      }
      if (item.consumer_discount && parseFloat(item.consumer_discount) < 0) {
        errors.push('Consumer discount cannot be negative');
      }
      if (item.doctor_discount && parseFloat(item.doctor_discount) < 0) {
        errors.push('Doctor discount cannot be negative');
      }
      if (item.stock && parseFloat(item.stock) < 0) errors.push('Stock cannot be negative');
      if (item.already_exists) errors.push('Mapping already exists');

      item.validation_errors = errors;
      item.is_valid = errors.length === 0;
    },

    async processBulkMapping() {
      if (!this.hasValidMappings) {
        this.$toast.error('No valid mappings to process');
        return;
      }

      this.isProcessing = true;
      
      try {
        const validMappings = this.mappingMatrix
          .filter(item => item.is_valid)
          .map(item => ({
            seller_id: item.seller_id,
            product_variant_id: item.product_variant_id,
            seller_product_id: item.seller_product_id,
            maximum_retail_price: parseFloat(item.maximum_retail_price) || 0,
            seller_discount: item.seller_discount ? parseFloat(item.seller_discount) : null,
            ht_discount: item.ht_discount ? parseFloat(item.ht_discount) : null,
            consumer_discount: item.consumer_discount ? parseFloat(item.consumer_discount) : null,
            doctor_discount: item.doctor_discount ? parseFloat(item.doctor_discount) : null,
            stock: parseInt(item.stock) || 0,
            status: item.status,
            is_active: item.is_active
          }));

        const response = await axios.post('/seller_product_variants/bulk_create', {
          mappings: validMappings
        });

        if (response.data.success) {
          this.resultsDialog = {
            show: true,
            success: true,
            created: response.data.created || 0,
            skipped: response.data.skipped || 0,
            failed: response.data.failed || 0,
            errors: response.data.errors || []
          };

          // Clear the matrix if all were successful
          if (response.data.failed === 0) {
            this.mappingMatrix = [];
            this.selectedProductVariants = [];
            this.selectedSellers = [];
          }
        }
      } catch (error) {
        console.error('Error processing bulk mapping:', error);
        this.$toast.error('Failed to process bulk mappings');
        
        this.resultsDialog = {
          show: true,
          success: false,
          created: 0,
          skipped: 0,
          failed: this.validMappingCount,
          errors: ['Server error occurred during processing']
        };
      } finally {
        this.isProcessing = false;
      }
    },

    closeResultsDialog() {
      this.resultsDialog.show = false;
      if (this.resultsDialog.created > 0) {
        this.$toast.success(`Successfully created ${this.resultsDialog.created} seller-variant mappings`);
      }
    },

    getSellerName(seller) {
      if (!seller) return 'N/A';
      return `${seller.first_name || ''} ${seller.last_name || ''}`.trim() || seller.email || 'N/A';
    },

    getSellerInitials(seller) {
      if (!seller) return 'NA';
      const firstName = seller.first_name || '';
      const lastName = seller.last_name || '';
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA';
    }
  }
};
</script>

<style scoped>
/* Professional Styling */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.control-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
}

.matrix-card {
  background: #ffffff;
}

.empty-state-card {
  background: #fafafa;
  border: 2px dashed #e0e0e0;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.matrix-field >>> .v-input__control {
  min-height: 32px;
}

.matrix-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding: 4px 8px;
}

.mapping-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.mapping-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.mapping-table >>> .v-data-table-header th {
  font-weight: 600;
  color: #424242;
  font-size: 0.75rem;
  padding: 0 8px;
}

.mapping-table >>> .v-data-table tbody tr td {
  padding: 4px 8px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
  
  .matrix-field >>> .v-input__control {
    min-height: 28px;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1rem;
  }
  
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Professional Transitions */
.v-card,
.v-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>